/**
 * StoryEditor
 * Inline editor for JIRA story details
 */

import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { ProposedStory, MeetingPriority } from '../../../types';
import { Input } from '../../ui/Input';
import { Textarea } from '../../ui/Textarea';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import styles from './StoryEditor.module.css';

export interface StoryEditorProps {
  story: ProposedStory;
  onUpdate: (story: ProposedStory) => void;
  onDelete: () => void;
}

export const StoryEditor: React.FC<StoryEditorProps> = ({ story, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedStory, setEditedStory] = useState(story);

  const handleSave = () => {
    onUpdate(editedStory);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedStory(story);
    setIsEditing(false);
  };

  const handleAddCriteria = () => {
    setEditedStory({
      ...editedStory,
      acceptanceCriteria: [...editedStory.acceptanceCriteria, ''],
    });
  };

  const handleUpdateCriteria = (index: number, value: string) => {
    const updated = [...editedStory.acceptanceCriteria];
    updated[index] = value;
    setEditedStory({ ...editedStory, acceptanceCriteria: updated });
  };

  const handleRemoveCriteria = (index: number) => {
    const updated = editedStory.acceptanceCriteria.filter((_, i) => i !== index);
    setEditedStory({ ...editedStory, acceptanceCriteria: updated });
  };

  const getPriorityColor = (priority: MeetingPriority) => {
    const colors = {
      High: 'error',
      Medium: 'warning',
      Low: 'info',
    };
    return colors[priority] || 'default';
  };

  if (!isEditing) {
    return (
      <div className={styles.storyCard} onClick={() => setIsEditing(true)}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h4 className={styles.title}>{story.title}</h4>
            <Badge variant={getPriorityColor(story.priority) as any}>
              {story.priority}
            </Badge>
          </div>
          <button
            className={styles.deleteButton}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            aria-label="Delete story"
          >
            <Trash2 size={16} />
          </button>
        </div>
        <p className={styles.description}>{story.description}</p>
        {story.storyPoints && (
          <div className={styles.points}>Story Points: {story.storyPoints}</div>
        )}
        {story.acceptanceCriteria.length > 0 && (
          <div className={styles.criteria}>
            <strong>Acceptance Criteria:</strong>
            <ul>
              {story.acceptanceCriteria.map((criteria, idx) => (
                <li key={idx}>{criteria}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.storyEditor}>
      <div className={styles.editorHeader}>
        <h4 className={styles.editorTitle}>Edit Story</h4>
        <button
          className={styles.closeButton}
          onClick={handleCancel}
          aria-label="Cancel editing"
        >
          <X size={18} />
        </button>
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Title</label>
        <Input
          value={editedStory.title}
          onChange={(e) => setEditedStory({ ...editedStory, title: e.target.value })}
          placeholder="Story title"
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Description</label>
        <Textarea
          value={editedStory.description}
          onChange={(e) => setEditedStory({ ...editedStory, description: e.target.value })}
          placeholder="Story description"
          rows={4}
        />
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label}>Priority</label>
          <select
            className={styles.select}
            value={editedStory.priority}
            onChange={(e) =>
              setEditedStory({ ...editedStory, priority: e.target.value as MeetingPriority })
            }
          >
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Story Points</label>
          <Input
            type="number"
            min="1"
            max="100"
            value={editedStory.storyPoints || ''}
            onChange={(e) =>
              setEditedStory({ ...editedStory, storyPoints: parseInt(e.target.value) || undefined })
            }
            placeholder="Points"
          />
        </div>
      </div>

      <div className={styles.field}>
        <div className={styles.criteriaHeader}>
          <label className={styles.label}>Acceptance Criteria</label>
          <Button variant="ghost" size="sm" onClick={handleAddCriteria}>
            <Plus size={16} />
            Add
          </Button>
        </div>
        <div className={styles.criteriaList}>
          {editedStory.acceptanceCriteria.map((criteria, index) => (
            <div key={index} className={styles.criteriaItem}>
              <Input
                value={criteria}
                onChange={(e) => handleUpdateCriteria(index, e.target.value)}
                placeholder="Enter acceptance criteria"
              />
              <button
                className={styles.removeCriteria}
                onClick={() => handleRemoveCriteria(index)}
                aria-label="Remove criteria"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.actions}>
        <Button variant="ghost" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default StoryEditor;
